package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.presentation.home.ClientReviewFormState
import com.patronat.top.client.presentation.home.ClientReviewMetric
import com.patronat.top.client.presentation.home.ClientReviewPhoto
import com.patronat.top.dto.VenueResponse
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_review_add_photo
import top.clientapp.generated.resources.client_review_atmosphere
import top.clientapp.generated.resources.client_review_detail_rating
import top.clientapp.generated.resources.client_review_max_photos
import top.clientapp.generated.resources.client_review_overall_rating
import top.clientapp.generated.resources.client_review_personal
import top.clientapp.generated.resources.client_review_photo_title
import top.clientapp.generated.resources.client_review_price
import top.clientapp.generated.resources.client_review_quality
import top.clientapp.generated.resources.client_review_speed
import top.clientapp.generated.resources.client_review_submit
import top.clientapp.generated.resources.client_review_text_placeholder
import top.clientapp.generated.resources.client_review_text_title
import top.clientapp.generated.resources.client_review_title
import top.clientapp.generated.resources.client_review_value_hint

@Composable
fun VenueReviewContent(
    venue: VenueResponse,
    form: ClientReviewFormState,
    isFavorite: Boolean,
    onBackClick: () -> Unit,
    onFavoriteClick: () -> Unit,
    onOverallRatingChange: (Int) -> Unit,
    onDetailRatingChange: (ClientReviewMetric, Int?) -> Unit,
    onTextChange: (String) -> Unit,
    onAddPhotoClick: () -> Unit,
    onRemovePhotoClick: (String) -> Unit,
    onSubmitClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(start = 24.dp, top = 18.dp, end = 24.dp, bottom = 102.dp),
            verticalArrangement = Arrangement.spacedBy(22.dp)
        ) {
            item {
                VenueReviewTopBar(
                    isFavorite = isFavorite,
                    onBackClick = onBackClick,
                    onFavoriteClick = onFavoriteClick
                )
            }

            item {
                VenueReviewSummary(venue = venue)
            }

            item {
                RatingSectionTitle(text = stringResource(Res.string.client_review_overall_rating))
                Spacer(modifier = Modifier.height(10.dp))
                OverallRatingPicker(
                    value = form.overallRating,
                    onValueChange = onOverallRatingChange
                )
                Spacer(modifier = Modifier.height(9.dp))
                Text(
                    text = stringResource(Res.string.client_review_value_hint),
                    style = BodyMedium.copy(
                        color = MineShaft500,
                        fontSize = 10.sp,
                        lineHeight = 15.sp,
                        fontWeight = FontWeight.Normal,
                        textAlign = TextAlign.Center
                    ),
                    modifier = Modifier.fillMaxWidth()
                )
            }

            item {
                RatingSectionTitle(text = stringResource(Res.string.client_review_detail_rating))
                Spacer(modifier = Modifier.height(13.dp))
                DetailedRatingRow(
                    label = stringResource(Res.string.client_review_price),
                    value = form.priceRating,
                    metric = ClientReviewMetric.Price,
                    onValueChange = onDetailRatingChange
                )
                DetailedRatingRow(
                    label = stringResource(Res.string.client_review_quality),
                    value = form.qualityRating,
                    metric = ClientReviewMetric.Quality,
                    onValueChange = onDetailRatingChange
                )
                DetailedRatingRow(
                    label = stringResource(Res.string.client_review_speed),
                    value = form.speedRating,
                    metric = ClientReviewMetric.Speed,
                    onValueChange = onDetailRatingChange
                )
                DetailedRatingRow(
                    label = stringResource(Res.string.client_review_personal),
                    value = form.staffRating,
                    metric = ClientReviewMetric.Staff,
                    onValueChange = onDetailRatingChange
                )
                DetailedRatingRow(
                    label = stringResource(Res.string.client_review_atmosphere),
                    value = form.atmosphereRating,
                    metric = ClientReviewMetric.Atmosphere,
                    onValueChange = onDetailRatingChange
                )
            }

            item {
                TextAreaBlock(
                    text = form.text,
                    onTextChange = onTextChange
                )
            }

            item {
                ReviewPhotoBlock(
                    photos = form.photos,
                    isUploading = form.isPhotoUploading,
                    onAddClick = onAddPhotoClick,
                    onRemoveClick = onRemovePhotoClick
                )
            }
        }

        AppButton(
            text = stringResource(Res.string.client_review_submit),
            onClick = onSubmitClick,
            type = ButtonType.Black,
            enabled = form.overallRating in 1..10 && !form.isSubmitting,
            isLoading = form.isSubmitting,
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .padding(horizontal = 24.dp, vertical = 20.dp)
        )
    }
}

@Composable
private fun VenueReviewTopBar(
    isFavorite: Boolean,
    onBackClick: () -> Unit,
    onFavoriteClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(28.dp)
    ) {
        Icon(
            painter = AppIcons.ArrowLeft,
            contentDescription = null,
            tint = MineShaft950,
            modifier = Modifier
                .align(Alignment.CenterStart)
                .size(24.dp)
                .clickable(onClick = onBackClick)
        )

        Text(
            text = stringResource(Res.string.client_review_title),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 14.sp,
                lineHeight = 21.sp,
                fontWeight = FontWeight.Medium,
                textAlign = TextAlign.Center
            ),
            modifier = Modifier.align(Alignment.Center)
        )

        Box(
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .size(28.dp)
                .clip(CircleShape)
                .background(White)
                .clickable(onClick = onFavoriteClick),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = if (isFavorite) AppIcons.HeartFilled else AppIcons.Heart,
                contentDescription = null,
                tint = Color.Unspecified,
                modifier = Modifier.size(23.dp)
            )
        }
    }
}

@Composable
private fun VenueReviewSummary(venue: VenueResponse) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val imageUrl = remember(venue.gallery) {
            venue.gallery.firstOrNull { !it.publicUrl.isNullOrBlank() }?.publicUrl
        }

        Box(
            modifier = Modifier
                .size(80.dp)
                .clip(RoundedCornerShape(18.dp))
                .background(MineShaft50),
            contentAlignment = Alignment.Center
        ) {
            if (imageUrl.isNullOrBlank()) {
                Icon(
                    painter = AppIcons.VenueImagePlaceholder,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(32.dp)
                )
            } else {
                AppNetworkImage(
                    model = imageUrl,
                    contentDescription = venue.name,
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        Column(
            modifier = Modifier.weight(1f)
        ) {
            Text(
                text = venue.name,
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 17.sp,
                    lineHeight = 25.5.sp,
                    letterSpacing = (-0.43).sp,
                    fontWeight = FontWeight.Medium
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            Spacer(modifier = Modifier.height(7.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(5.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.VenueLocation,
                    contentDescription = null,
                    tint = MineShaft400,
                    modifier = Modifier.size(12.dp)
                )

                Text(
                    text = venue.location.address,
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 14.sp,
                        lineHeight = 24.sp,
                        fontWeight = FontWeight.Normal,
                        letterSpacing = (-0.31).sp
                    ),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.height(7.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.RatingStarFilled,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(16.dp)
                )

                Text(
                    text = venue.ratingSummaryText(),
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 13.sp,
                        lineHeight = 18.sp,
                        fontWeight = FontWeight.Normal
                    ),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun RatingSectionTitle(text: String) {
    Text(
        text = text,
        style = BodyMedium.copy(
            color = MineShaft900,
            fontSize = 20.sp,
            lineHeight = 30.sp,
            fontWeight = FontWeight.Medium,
            letterSpacing = (-0.45).sp
        )
    )
}

@Composable
private fun OverallRatingPicker(
    value: Int,
    onValueChange: (Int) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(56.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(MineShaft50)
            .padding(horizontal = 8.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        RatingStars(
            value = value,
            starSize = 24,
            enabled = false,
            onValueChange = onValueChange
        )
    }
}

@Composable
private fun DetailedRatingRow(
    label: String,
    value: Int?,
    metric: ClientReviewMetric,
    onValueChange: (ClientReviewMetric, Int?) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 10.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 14.sp,
                    lineHeight = 21.sp,
                    fontWeight = FontWeight.Medium,
                    letterSpacing = (-0.15).sp
                )
            )

            Text(
                text = value?.toString() ?: "-",
                style = BodyMedium.copy(
                    color = MineShaft400,
                    fontSize = 14.sp,
                    lineHeight = 21.sp,
                    fontWeight = FontWeight.Normal,
                    letterSpacing = (-0.15).sp,
                    textAlign = TextAlign.End
                )
            )
        }

        Spacer(modifier = Modifier.height(8.dp))

        RatingStars(
            value = value ?: 0,
            starSize = 20,
            onValueChange = { next ->
                onValueChange(metric, if (next == value) null else next)
            }
        )
    }
}

@Composable
private fun RatingStars(
    value: Int,
    starSize: Int,
    horizontalArrangement: Arrangement.Horizontal = Arrangement.SpaceBetween,
    enabled: Boolean = true,
    onValueChange: (Int) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = horizontalArrangement,
        verticalAlignment = Alignment.CenterVertically
    ) {
        for (rating in 1..10) {
            RatingStarIcon(
                selected = rating <= value,
                starSize = starSize,
                enabled = enabled,
                onClick = { onValueChange(rating) }
            )
        }
    }
}

@Composable
private fun RatingStarIcon(
    selected: Boolean,
    starSize: Int,
    enabled: Boolean,
    onClick: () -> Unit
) {
    val color = if (selected) Primary600 else MineShaft200
    Canvas(
        modifier = Modifier
            .size(starSize.dp)
            .clickable(enabled = enabled, onClick = onClick)
    ) {
        val centerX = size.width / 2f
        val centerY = size.height / 2f
        val outerRadius = size.minDimension * 0.48f
        val innerRadius = outerRadius * 0.48f
        val path = Path()

        for (point in 0 until 10) {
            val angle = -PI / 2.0 + point * PI / 5.0
            val radius = if (point % 2 == 0) outerRadius else innerRadius
            val x = centerX + cos(angle).toFloat() * radius
            val y = centerY + sin(angle).toFloat() * radius

            if (point == 0) {
                path.moveTo(x, y)
            } else {
                path.lineTo(x, y)
            }
        }
        path.close()

        if (selected) {
            drawPath(path = path, color = color)
        } else {
            drawPath(
                path = path,
                color = color,
                style = Stroke(width = 1.25.dp.toPx())
            )
        }
    }
}

@Composable
private fun TextAreaBlock(
    text: String,
    onTextChange: (String) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        RatingSectionTitle(text = stringResource(Res.string.client_review_text_title))

        Text(
            text = "${text.length}/500",
            style = BodyMedium.copy(
                color = MineShaft500,
                fontSize = 12.sp,
                lineHeight = 18.sp,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.End
            )
        )
    }

    Spacer(modifier = Modifier.height(10.dp))

    BasicTextField(
        value = text,
        onValueChange = onTextChange,
        textStyle = BodyMedium.copy(
            color = MineShaft500,
            fontSize = 16.sp,
            lineHeight = 25.6.sp,
            letterSpacing = 0.sp,
            fontWeight = FontWeight.Normal
        ),
        modifier = Modifier
            .fillMaxWidth()
            .height(132.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(MineShaft50)
            .border(1.dp, MineShaft100, RoundedCornerShape(12.dp)),
        decorationBox = { innerTextField ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 16.dp, vertical = 14.dp)
            ) {
                if (text.isBlank()) {
                    Text(
                        text = stringResource(Res.string.client_review_text_placeholder),
                        style = BodyMedium.copy(
                            color = MineShaft500,
                            fontSize = 16.sp,
                            lineHeight = 25.6.sp,
                            letterSpacing = 0.sp,
                            fontWeight = FontWeight.Normal
                        )
                    )
                }
                innerTextField()
            }
        }
    )
}

@Composable
private fun ReviewPhotoBlock(
    photos: List<ClientReviewPhoto>,
    isUploading: Boolean,
    onAddClick: () -> Unit,
    onRemoveClick: (String) -> Unit
) {
    RatingSectionTitle(text = stringResource(Res.string.client_review_photo_title))

    Spacer(modifier = Modifier.height(10.dp))

    LazyRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        items(
            items = photos.take(MaxPhotoCount),
            key = { it.mediaId }
        ) { photo ->
            ReviewPhotoTile(
                photo = photo,
                onRemoveClick = onRemoveClick
            )
        }

        if (photos.size < MaxPhotoCount) {
            item {
                AddPhotoTile(
                    isUploading = isUploading,
                    onClick = onAddClick
                )
            }
        }
    }

    Spacer(modifier = Modifier.height(6.dp))

    Text(
        text = stringResource(Res.string.client_review_max_photos),
        style = BodyMedium.copy(
            color = MineShaft500,
            fontSize = 10.sp,
            lineHeight = 15.sp,
            fontWeight = FontWeight.Normal
        )
    )
}

@Composable
private fun ReviewPhotoTile(
    photo: ClientReviewPhoto,
    onRemoveClick: (String) -> Unit
) {
    Box(
        modifier = Modifier
            .size(96.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(MineShaft50),
        contentAlignment = Alignment.Center
    ) {
        AppNetworkImage(
            model = photo.publicUrl,
            contentDescription = null,
            modifier = Modifier.fillMaxSize()
        )

        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(top = 8.dp, end = 8.dp)
                .size(24.dp)
                .clip(CircleShape)
                .background(White)
                .clickable { onRemoveClick(photo.mediaId) },
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = AppIcons.Close,
                contentDescription = null,
                tint = MineShaft950,
                modifier = Modifier.size(12.dp)
            )
        }
    }
}

@Composable
private fun AddPhotoTile(
    isUploading: Boolean,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .size(96.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(MineShaft50)
            .clickable(enabled = !isUploading, onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        if (isUploading) {
            CircularProgressIndicator(
                color = Primary600,
                strokeWidth = 2.dp,
                modifier = Modifier.size(18.dp)
            )
        } else {
            Icon(
                painter = AppIcons.Upload,
                contentDescription = null,
                tint = MineShaft950,
                modifier = Modifier.size(22.dp)
            )
        }

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = stringResource(Res.string.client_review_add_photo),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 11.sp,
                lineHeight = 15.sp,
                fontWeight = FontWeight.Medium,
                textAlign = TextAlign.Center
            ),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private const val MaxPhotoCount = 5
